export type RouteSurface =
  | 'public-landing'
  | 'authentication'
  | 'signup'
  | 'member-application'
  | 'settings'
  | 'governor'
  | 'call-runtime'
  | 'system'
  | 'unknown';

export interface RouteAuthorityDecision {
  readonly pathname: string;
  readonly surface: RouteSurface;
  readonly publicRoute: boolean;
  readonly authenticationRoute: boolean;
  readonly signupRoute: boolean;
  readonly memberApplicationRoute: boolean;
  readonly settingsRoute: boolean;
  readonly governorRoute: boolean;
  readonly callRuntimeRoute: boolean;
  readonly appLayoutAllowed: boolean;
}

const normalizePathname = (pathname: string): string => {
  const cleaned = String(pathname || '/')
    .trim()
    .split('?')[0]
    .split('#')[0];

  if (!cleaned || cleaned === '/') {
    return '/';
  }

  return cleaned.endsWith('/')
    ? cleaned.slice(0, -1)
    : cleaned;
};

const AUTHENTICATION_PATHS = new Set([
  '/login',
  '/log-in',
  '/signin',
  '/auth/callback',
  '/reset-password',
]);

const SIGNUP_PATHS = new Set([
  '/signup',
  '/join',
  '/invite',
]);

const MEMBER_APPLICATION_PATHS = new Set([
  '/app',
  '/dashboard',
]);

const SETTINGS_PATHS = new Set([
  '/settings',
  '/account',
  '/membership',
]);

export function resolveRouteAuthority(
  pathname: string,
): RouteAuthorityDecision {
  const normalized = normalizePathname(pathname);

  const publicRoute = normalized === '/';

  const authenticationRoute =
    AUTHENTICATION_PATHS.has(normalized);

  const signupRoute =
    SIGNUP_PATHS.has(normalized) ||
    normalized.startsWith('/join/') ||
    normalized.startsWith('/invite/');

  const governorRoute =
    normalized === '/governor' ||
    normalized.startsWith('/governor-');

  const callRuntimeRoute =
    normalized === '/native-call' ||
    normalized.startsWith('/native-call/') ||
    normalized === '/call-ended';

  const memberApplicationRoute =
    MEMBER_APPLICATION_PATHS.has(normalized);

  const settingsRoute =
    SETTINGS_PATHS.has(normalized);

  let surface: RouteSurface = 'unknown';

  if (publicRoute) {
    surface = 'public-landing';
  } else if (authenticationRoute) {
    surface = 'authentication';
  } else if (signupRoute) {
    surface = 'signup';
  } else if (governorRoute) {
    surface = 'governor';
  } else if (callRuntimeRoute) {
    surface = 'call-runtime';
  } else if (memberApplicationRoute) {
    surface = 'member-application';
  } else if (settingsRoute) {
    surface = 'settings';
  } else if (
    normalized.startsWith('/admin/') ||
    normalized === '/system-health'
  ) {
    surface = 'system';
  }

  return Object.freeze({
    pathname: normalized,
    surface,
    publicRoute,
    authenticationRoute,
    signupRoute,
    memberApplicationRoute,
    settingsRoute,
    governorRoute,
    callRuntimeRoute,
    appLayoutAllowed:
      publicRoute || memberApplicationRoute,
  });
}

export function assertAppLayoutAuthority(
  pathname: string,
): RouteAuthorityDecision {
  const decision = resolveRouteAuthority(pathname);

  if (!decision.appLayoutAllowed) {
    throw new Error(
      `Route Authority denied AppLayout ownership for ${decision.pathname}`,
    );
  }

  return decision;
}

export type PostCallExitDestination = '/app' | '/login';

export interface PostCallExitDecision {
  readonly source: 'call-ended';
  readonly authenticated: boolean;
  readonly requestedDestination: '/';
  readonly authorizedDestination: PostCallExitDestination;
  readonly reason:
    | 'authenticated-member-return'
    | 'unauthenticated-login-required';
}

/**
 * Canonical post-call route decision.
 *
 * Call pages must request this decision rather than navigating directly
 * to the public landing route.
 */
export function resolvePostCallExitDestination(
  authenticated: boolean,
): PostCallExitDecision {
  return Object.freeze({
    source: 'call-ended',
    authenticated,
    requestedDestination: '/',
    authorizedDestination: authenticated ? '/app' : '/login',
    reason: authenticated
      ? 'authenticated-member-return'
      : 'unauthenticated-login-required',
  });
}
